import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';
import GuideHelpButton from './GuideHelpButton';
import { galleryGuideApplyEvent, readGalleryGuideHidden, refreshGalleryGuideStorage } from '../utils/galleryGuidePreference';

export default function IllustrationScrollActions({ favorites = false, showGuide = true }: { favorites?: boolean; showGuide?: boolean }) {
    const { t } = useContent();
    const [position, setPosition] = useState({ top: 160, outside: false });
    const [hidden, setHidden] = useState(readGalleryGuideHidden);
    const [leaving, setLeaving] = useState(false);
    useEffect(() => {
        let exitTimer = 0, anchorTimer = 0, focusFrame = 0;
        let restoreAnchor = () => {};
        const clear = () => {
            clearTimeout(exitTimer); clearTimeout(anchorTimer); cancelAnimationFrame(focusFrame); restoreAnchor();
        };
        const apply = () => {
            clear();
            const next = readGalleryGuideHidden();
            const icon = document.querySelector<HTMLElement>('.lab-floating-guide');
            if (!next || !showGuide || !icon) { setHidden(next); setLeaving(false); return; }
            const main = document.querySelector<HTMLElement>('main');
            const previousAnchor = main?.style.overflowAnchor || '';
            if (main) main.style.overflowAnchor = 'none';
            restoreAnchor = () => { if (main) main.style.overflowAnchor = previousAnchor; };
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const finish = () => {
                setHidden(true); setLeaving(false);
                focusFrame = requestAnimationFrame(() => {
                    const active = document.activeElement;
                    if (!document.querySelector('[role="dialog"]') && (active === document.body || active?.closest('.lab-floating-guide') || (reduced && active?.matches('.lab-menu-button')))) {
                        const heading = document.querySelector<HTMLElement>('main h1') || main;
                        if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
                    }
                });
                anchorTimer = window.setTimeout(restoreAnchor, reduced ? 0 : 320);
            };
            setLeaving(true);
            if (reduced) finish(); else exitTimer = window.setTimeout(finish, 240);
        };
        window.addEventListener(galleryGuideApplyEvent, apply);
        window.addEventListener('storage', refreshGalleryGuideStorage);
        return () => { clear(); window.removeEventListener(galleryGuideApplyEvent, apply); window.removeEventListener('storage', refreshGalleryGuideStorage); };
    }, [showGuide]);
    useLayoutEffect(() => {
        const update = () => {
            const scroller = document.querySelector<HTMLElement>('.lab-favorites-scroll');
            const top = (scroller?.getBoundingClientRect().top ?? document.querySelector('.lab-subheader')?.getBoundingClientRect().bottom ?? 64) + 8;
            const conditions = document.querySelector<HTMLElement>('.lab-filter-chips');
            const bounds = conditions?.getBoundingClientRect();
            const next = { top, outside: bounds ? bounds.bottom <= top : (scroller?.scrollTop ?? window.scrollY) > 0 };
            setPosition(previous => previous.top === next.top && previous.outside === next.outside ? previous : next);
        };
        const resize = new ResizeObserver(update);
        for (const selector of ['.lab-header', '.lab-subheader', '.lab-favorites-navigation']) {
            const element = document.querySelector(selector); if (element) resize.observe(element);
        }
        const mutation = new MutationObserver(update);
        mutation.observe(document.body, { childList: true, subtree: true });
        document.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        update();
        return () => { resize.disconnect(); mutation.disconnect(); document.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
    }, []);
    const backToTop = () => {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        const scroller = document.querySelector<HTMLElement>('.lab-favorites-scroll');
        if (scroller) scroller.scrollTo({ top: 0, behavior });
        else window.scrollTo({ top: 0, behavior });
        const heading = document.querySelector<HTMLElement>('main h1');
        if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    };
    const backButton = position.outside ? <button type="button" className="lab-back-top" aria-label={t('illustrations.backTop')} title={t('illustrations.backTop')} onClick={backToTop}><ArrowUp aria-hidden="true" /></button> : null;
    if (!showGuide || hidden) return backButton;
    return <div className="lab-scroll-actions">
        <div className={`lab-floating-guide${leaving ? ' lab-guide-leaving' : ''}`} inert={leaving || undefined} aria-hidden={leaving || undefined} style={{ top: position.top }}><GuideHelpButton section={favorites ? 'favorites' : 'search'} source="gallery-float" /></div>
        {backButton}
    </div>;
}
